import React, { useState } from "react";
import { createPortal } from "react-dom";
import { EditorState } from "draft-js";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { Editor } from "react-draft-wysiwyg";
import { FormikProps } from "formik";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { EDITOR_MODE } from "../../utils/AppEnumerations";
import { prepareMessageFromParams } from "../../utils/AppFunctions";
import { sanitizeRichHtml } from "../../utils/RichHtmlSanitizer";
import ICConfirmationModal from "./ICConfirmationModal";
import ICRichHtmlContent from "./ICRichHtmlContent";
import {
  createEditorState,
  editorStateToHtml,
  isRichTextCompatible,
} from "./CustomEditorHtml";
import { cn } from "../../lib";

interface ICustomEditorProps {
  formikPorps: FormikProps<any>;
  fieldName: string;
  text: string;
  displayName?: string;
  enableHtmlMode?: boolean;
}

interface IHtmlModeButtonProps {
  active: boolean;
  title: string;
  onClick: () => void;
}

const HtmlModeButton: React.FC<IHtmlModeButtonProps> = ({
  active,
  title,
  onClick,
}) => (
  <div className="rdw-inline-wrapper">
    <div
      role="button"
      title={title}
      aria-pressed={active}
      onClick={onClick}
      className={cn("rdw-option-wrapper gap-1 !px-2 text-xs font-semibold", {
        "rdw-option-active": active,
      })}
    >
      <span className="font-mono">{"</>"}</span>
      <span>HTML</span>
    </div>
  </div>
);

const CustomEditor: React.FC<ICustomEditorProps> = ({
  formikPorps,
  fieldName,
  text,
  displayName,
  enableHtmlMode = false,
}) => {
  const { t } = UseTranslationHook();
  const [editorState, setEditorState] = useState<EditorState>(() =>
    createEditorState(text || ""),
  );
  const [mode, setMode] = useState<EDITOR_MODE>(() =>
    enableHtmlMode && text && !isRichTextCompatible(text)
      ? EDITOR_MODE.Html
      : EDITOR_MODE.Rich,
  );
  const [htmlSource, setHtmlSource] = useState(text || "");
  const [showRichTextWarning, setShowRichTextWarning] = useState(false);
  const sanitizedHtml = sanitizeRichHtml(
    mode === EDITOR_MODE.Html ? htmlSource : "",
  );

  const toFieldValue = (html: string) =>
    enableHtmlMode ? sanitizeRichHtml(html).html : html;

  const onEditorStateChange = (editorState: EditorState) => {
    setEditorState(editorState);
    formikPorps.setFieldValue(
      fieldName,
      toFieldValue(editorStateToHtml(editorState)),
    );
  };

  const onHtmlSourceChange = (value: string) => {
    setHtmlSource(value);
    formikPorps.setFieldValue(fieldName, toFieldValue(value));
  };

  const switchToHtmlMode = () => {
    onHtmlSourceChange(editorStateToHtml(editorState));
    setMode(EDITOR_MODE.Html);
  };

  const applyRichTextMode = (html: string) => {
    setMode(EDITOR_MODE.Rich);
    if (html) {
      onEditorStateChange(createEditorState(html));
      return;
    }
    setEditorState(EditorState.createEmpty());
    formikPorps.setFieldValue(fieldName, "");
  };

  const requestRichTextMode = () => {
    if (isRichTextCompatible(sanitizedHtml.html)) {
      applyRichTextMode(sanitizedHtml.html);
    } else {
      setShowRichTextWarning(true);
    }
  };

  return (
    <div className="editor-border">
      <div className="editor-bottom-border">
        <p className="ml-2 p-2 font-semibold">{displayName}</p>
      </div>
      <div className="p-3">
        {mode === EDITOR_MODE.Rich ? (
          <Editor
            editorState={editorState}
            toolbar={{
              options: [
                "inline",
                "blockType",
                "list",
                "link",
                "image",
                "colorPicker",
              ],
            }}
            toolbarCustomButtons={
              enableHtmlMode
                ? [
                    <HtmlModeButton
                      key="html-mode"
                      active={false}
                      title={t("edit_as_html")}
                      onClick={switchToHtmlMode}
                    />,
                  ]
                : undefined
            }
            onEditorStateChange={onEditorStateChange}
          />
        ) : (
          <div className="space-y-3">
            <div className="rdw-editor-toolbar !mb-0">
              <HtmlModeButton
                active
                title={t("back_to_rich_text_editor")}
                onClick={requestRichTextMode}
              />
            </div>
            <div>
              <p className="mb-1 font-semibold">{t("html_code")}</p>
              <textarea
                value={htmlSource}
                onChange={(e) => onHtmlSourceChange(e.target.value)}
                placeholder={t("html_source_placeholder")}
                spellCheck={false}
                rows={12}
                className="w-full rounded border border-base p-2 font-mono text-sm outline-none focus:border-accent"
              />
              {sanitizedHtml.removed.length > 0 && (
                <div className="text-sm text-warning">
                  {prepareMessageFromParams(t("html_content_removed"), [
                    ["items", sanitizedHtml.removed.join(", ")],
                  ])}
                </div>
              )}
            </div>
            <div>
              <p className="mb-1 font-semibold">{t("html_preview")}</p>
              <div className="min-h-12 rounded border border-base p-3">
                <ICRichHtmlContent html={sanitizedHtml.html} />
              </div>
            </div>
          </div>
        )}
      </div>
      {showRichTextWarning &&
        createPortal(
          <ICConfirmationModal
            title={t("switch_to_rich_text_title")}
            message={t("switch_to_rich_text_message")}
            onClose={(confirm) => {
              setShowRichTextWarning(false);
              if (confirm) applyRichTextMode("");
            }}
          />,
          document.body,
        )}
    </div>
  );
};

export default CustomEditor;
